





































































import Vue from 'vue'
import Interval from '@/components/Common/Interval'
import { Col, Row, Button } from 'element-ui'
import { WRTCServer } from '@/types/admin'
import SubscribeWowzaStats from '@/mixins/SubscribeWowzaStats'
import './WRTCServerSummary.scss'
import { WowzaStat, WOWZA_CONNECTION_LIMIT, WOWZA_THREAD_LIMIT } from '@/types/wowza'
import { StatusColor } from '.'
import { firestore } from 'firebase/app'


export default Vue.extend({
  name: 'WRTCServerSummary',
  mixins: [SubscribeWowzaStats],
  props: {
    server: {
      type: Object as () => WRTCServer,
      required: true
    },
    serverid: {
      type: String,
      required: true
    },
    deleteButton: {
      type: Boolean,
      required: false,
      default: true
    }
  },
  data: () => ({
    now: 0,
    collapsed: true
  }),
  computed: {
    url (): string {
      return this.server.url
    },
    stats (): WowzaStat|undefined {
      return this.$store.direct.state.opsmonitor.wowzastats[this.serverid]
    },
    connections (): string {
      return '' + (this.stats?.connectionCount ?? 'unknown')
    },
    connectionColor (): StatusColor {
      if (this.stats?.connectionCount === undefined) return StatusColor.GRAY
      return this.stats.connectionCount > WOWZA_CONNECTION_LIMIT ? StatusColor.RED : StatusColor.GREEN
    },
    threads (): string {
      return '' + (this.stats?.threadCount ?? 'unknown')
    },
    threadsColor (): StatusColor {
      if (this.stats?.threadCount === undefined) return StatusColor.GRAY
      if (this.stats.threadCount === 0) return StatusColor.GRAY
      return this.stats.threadCount > WOWZA_THREAD_LIMIT ? StatusColor.RED : StatusColor.GREEN
    },
    cpu (): string {
      if (this.stats === undefined) return 'unknown'
      return `Service: ${this.stats.cpuUser}% / System: ${this.stats.cpuSystem}%`
    },
    cpuColor (): StatusColor {
      if (this.stats === undefined) return StatusColor.GRAY
      if (this.stats.cpuIdle === 0 && this.stats.cpuUser === 0 && this.stats.cpuSystem === 0) return StatusColor.GRAY
      return this.stats.cpuIdle > 30 ? StatusColor.GREEN : StatusColor.RED
    },
    memory (): string {
      if (this.stats === undefined) return 'unknown'
      return this.sizeSummary(this.stats.memoryFree, this.stats.memoryUsed)
    },
    memoryColor (): StatusColor {
      if (this.stats === undefined) return StatusColor.GRAY
      if (this.stats.memoryFree === 0 && this.stats.memoryUsed === 0) return StatusColor.GRAY
      const percent = this.stats.memoryFree / (this.stats.memoryFree + this.stats.memoryUsed) * 100
      return percent > 15 ? StatusColor.GREEN : StatusColor.RED
    },
    heap (): string {
      if (this.stats === undefined) return 'unknown'
      return this.sizeSummary(this.stats.heapFree, this.stats.heapUsed)
    },
    heapColor (): StatusColor {
      if (this.stats === undefined) return StatusColor.GRAY
      if (this.stats.heapFree === 0 && this.stats.heapUsed === 0) return StatusColor.GRAY
      const percent = this.stats.heapFree / (this.stats.heapFree + this.stats.heapUsed) * 100
      return percent > 15 ? StatusColor.GREEN : StatusColor.RED
    },
    disk (): string {
      if (this.stats === undefined) return 'unknown'
      return this.sizeSummary(this.stats.diskFree, this.stats.diskUsed)
    },
    diskColor (): StatusColor {
      if (this.stats === undefined) return StatusColor.GRAY
      if (this.stats.diskFree === 0 && this.stats.diskUsed === 0) return StatusColor.GRAY
      const percent = this.stats.diskFree / (this.stats.diskFree + this.stats.diskUsed) * 100
      return percent > 15 ? StatusColor.GREEN : StatusColor.RED
    },
    serverUptime (): string {
      if (this.stats === undefined) return 'unknown'
      return this.secondsToTime(this.stats.serverUptime)
    },
    lastUpdated (): string {
      if (this.stats === undefined) return 'unknown'
      const seconds = this.now - this.stats.lastUpdate.seconds
      if (seconds > 300) return 'offline'
      return `${seconds} s ago`
    },
    lastUpdatedColor (): StatusColor {
      if (this.stats === undefined) return StatusColor.GRAY
      return this.lastUpdated === 'offline' ? StatusColor.RED : StatusColor.GREEN
    },
    summaryColor (): StatusColor {
      const colors = [this.lastUpdatedColor, this.diskColor, this.heapColor, this.memoryColor,
                      this.cpuColor, this.threadsColor, this.connectionColor]
      if (colors.every(color => color === StatusColor.GRAY)) return StatusColor.GRAY
      return (colors.some(color => color === StatusColor.RED)) ? StatusColor.RED : StatusColor.GREEN
    }
  },
  methods: {
    sizeSummary (free: number, used: number): string {
      let percent = free / (used + free) * 100
      if (isNaN(percent)) percent = 0
      const percentStr = this.formatNumber(percent)
      return `${this.formatSize(used)} (${percentStr}% free)`
    },
    formatSize (value: number): string {
      return this.formatNumber(value / 1024 / 1024 / 1024) + 'GB'
    },
    formatNumber (value: number): string {
      return '' + (Math.round(value * 10) / 10)
    },
    secondsToTime (seconds: number): string {
      const days = Math.floor(seconds / (24 * 3600))
      const hours = Math.floor((seconds - days * 3600 * 24) / 3600)
      const minutes = Math.floor((seconds - (days * 3600 * 24) - hours * 3600) / 60)
      return `${days}d ${hours}h ${minutes}m`
    },
    deleteServer () {
      this.$confirm('Are you sure you want to delete this server from the region?', 'Delete Wowza Server').then(() => {
        this.$store.direct.dispatch.admin.deleteWowzaServer(this.serverid).then(() => {
          this.$message('Wowza server deleted from region')
        }).catch(error => {
          this.$message('Could not delete wowza server ' + error.message)
        })
      }).catch(_error => {})
    },
    clockUpdate () {
      this.now = firestore.Timestamp.now().seconds
    }
  },
  components: {
    'el-col': Col,
    'el-row': Row,
    'el-button': Button,
    Interval
  }
})
